import { ChangeDetectionStrategy, Component, OnDestroy, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { MembersApiService } from '@lib/members/data-access';
import { PackageDescriptionComponent } from '@lib/members/ui';
import {
  GuaranteeApplicationStatus,
  GuaranteeStatus,
  MedicalServiceRequestBody,
  Prescription,
  PrescriptionTypes,
  PrescriptionsService,
  clearPrescriptionDrafts,
  convertDraftMetadata,
  selectRelatedSpecialistDraft,
} from '@lib/prescriptions/data-access';
import { PrescriptionDraftListItemComponent, ServicePrescriptionFormModalComponent } from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { TaskDataTargetComponent } from '../../../components/commons/task-data-target/task-data-target.component';
import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { Task, TaskStatus, TaskType } from '../../../core/models/task.interface';
import * as TaskActions from '../../../core/store/task.actions';
import { MultilineTextComponent } from '../../commons/multiline-text/multiline-text.component';

interface ServiceRequestFormControls {
  description: FormControl<string | null>;
  sendGuaranteeNotification: FormControl<boolean | null>;
}

@Component({
  selector: 'app-service-request-task-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatCheckbox,
    MatIcon,
    TranslatePipe,
    PrescriptionDraftListItemComponent,
    MultilineTextComponent,
    TaskDataTargetComponent,
    LoaderComponent,
    PackageDescriptionComponent,
  ],
  templateUrl: './service-request-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-6' },
})
export class ServiceRequestTaskDetailsComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly membersApiService = inject(MembersApiService);

  protected readonly formatDate = formatDate;
  protected readonly taskStatus = TaskStatus;

  protected readonly relatedSpecialistDraft = this.store.selectSignal(selectRelatedSpecialistDraft);

  protected readonly loading = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly task = inject<Task<TaskType.ServiceRequest>>(TASK_DATA);
  protected readonly memberPackage = toSignal(this.membersApiService.getMemberPackageByMemberId(this.task.data.memberId));
  protected readonly form = new FormGroup<ServiceRequestFormControls>({
    description: new FormControl<string | null>(null, [Validators.required]),
    sendGuaranteeNotification: new FormControl<boolean | null>(true),
  });

  private readonly draftDescriptionEffect = effect(() => {
    if (this.relatedSpecialistDraft()) this.form.controls.description.setValue(this.buildDescription(this.relatedSpecialistDraft().name, this.task.data.notes));
    else this.form.controls.description.setValue(null);
  });

  private buildDescription(name: string, notes: string): string {
    const lines = notes?.split('\n') ?? [];
    const answers = lines.filter((_, i) => i % 2 === 1);
    const base = this.translate.instant('prescription.message.serviceRequestDescription', { name });
    return answers.length ? `${base}. ${answers.join(' -> ')}` : base;
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearPrescriptionDrafts());
  }

  protected openSpecialistModal(): void {
    const existing = this.relatedSpecialistDraft();
    this.dialog.open(ServicePrescriptionFormModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      data: {
        prescription: existing as unknown as Prescription,
        prescriptionType: PrescriptionTypes.RelatedSpecialists,
        memberId: this.task.data.memberId,
        guaranteeApplicationId: null,
      },
    });
  }

  protected submit(): void {
    const draft = this.relatedSpecialistDraft();
    if (!draft) return;

    this.loading.set(true);
    this.errorMessage.set(null);

    const endDate = new Date(draft.endDate);
    endDate.setHours(23, 59, 59, 999);

    const prescription = {
      type: draft.type,
      name: draft.name,
      startDate: draft.startDate,
      endDate,
      metadata: convertDraftMetadata(draft),
      completedInAppointment: draft.completedInAppointment,
      completionReason: draft.completedInAppointment ? draft.completionReason : null,
      guaranteeStatus: draft.guaranteeStatus,
      reason: draft.reason,
      sendGuaranteeNotification: draft.sendGuaranteeNotification,
      franchise: draft.franchise,
    };

    const body: MedicalServiceRequestBody = {
      description: this.form.value.description,
      prescriptions: [prescription],
      appointmentId: null,
      sendGuaranteeNotification: this.form.value.sendGuaranteeNotification,
      status: GuaranteeApplicationStatus.Processed,
    };

    this.prescriptionsService
      .postMedicalService(this.task.data.memberId, body)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const resolution =
            prescription.guaranteeStatus === GuaranteeStatus.Guaranteed
              ? this.translate.instant('task.message.serviceRequestGuaranteed')
              : this.translate.instant('task.message.serviceRequestNotGuaranteed');
          this.store.dispatch(TaskActions.resolveTask({ task: this.task, payload: { resolution } }));
          this.router.navigate(['/tasks']);
        },
        error: err => {
          this.loading.set(false);
          this.errorMessage.set(err?.error?.Message ?? null);
        },
      });
  }

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }
}
